import React from 'react';
import FadeInSection from '../components/FadeInSection';

const CV: React.FC = () => {

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <FadeInSection className="mb-12" initialVisible={true}>
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-text-white mb-6">
            Thomas Benoit
          </h1>
        </div>
      </FadeInSection>

      {/* Experience Section */}
      <FadeInSection className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-text-white border-b border-text-gray pb-2">
          Experience
        </h2>
        
        <div className="space-y-6">
          <div className="border border-gray-600 rounded-lg p-6">
            <div className="flex justify-between items-start mb-4 flex-wrap">
              <div>
                <h3 className="text-xl font-semibold text-text-white">Software Engineer II</h3>
                <p className="text-text-gray italic">Carnegie Mellon University, Software Engineering Institute</p>
              </div>
              <div className="text-text-gray italic">
                January 2024 -- Present
              </div>
            </div>
            
            <ul className="space-y-4 text-text-gray">
              <li className="leading-relaxed">
                • Engineered and operationalized software foundational to the support of enterprise security monitoring tools. Supported customer cloud adoption by designing AWS environments and writing bespoke cloud-native network security solutions.
              </li>
              
              <li className="leading-relaxed">
                • Designed and implemented novel VPC Flow Log optimization strategy across AWS Virtual Private Cloud environments by identifying compression opportunities through cloud networking infrastructure analysis. Implemented multiple automated ETL (Extract, Transform, Load) pipelines to perform optimizations, resulting in a 60 percent reduction in log size while preserving full analytic and security monitoring value. The approach was subsequently adopted for Microsoft Azure environments. <span className="italic">(AWS, Python, Terraform, Lambda, SNS, SQS, Splunk, CloudWatch)</span>
              </li>
              
              <li className="leading-relaxed">
                • Designed, implemented, and deployed the first cloud-based instance of <a 
                  href="https://tools.netsa.cert.org/yaf/" 
                  className="text-primary hover:text-text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  YAF
                </a>. Engineered functionality to perform VxLAN and Geneve Layer-2 overlay network virtualization protocol decapsulation and extraction of key values. Enabled customers to modernize their security posture into the cloud by leveraging advanced network telemetry collection. <span className="italic">(AWS, C, Lua, IPFIX, Wireshark, TCP/IP)</span>
              </li>
            </ul>
          </div>

          <div className="border border-gray-600 rounded-lg p-6">
            <div className="flex justify-between items-start mb-4 flex-wrap">
              <div>
                <h3 className="text-xl font-semibold text-text-white">Software Engineer I</h3>
                <p className="text-text-gray italic">Carnegie Mellon University, Software Engineering Institute</p>
              </div>
              <div className="text-text-gray italic">
                May 2022 -- December 2023
              </div>
            </div>
            
            <ul className="space-y-4 text-text-gray">
              <li className="leading-relaxed">
                • Developed a standalone Python program to convert cloud flow logs into SiLK binary flows, taking ownership of the entire technical approach from algorithm design through deployment. Established integration standards for existing security tooling, managed persistent configuration files for consistent sensor ID labeling, and collaborated with network analysts to align with operational security needs. Reduced log storage requirements by 75 percent. <span className="italic">(Python, NetFlow, SiLK)</span>
              </li>
              
              <li className="leading-relaxed">
                • Developed and maintained the <a 
                  href="https://tools.netsa.cert.org/" 
                  className="text-primary hover:text-text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NetSA security tool suite
                </a>, an open-source suite of C tools used for collecting and analyzing network flow data. Used by government agencies and ISP partners, the tools touch over 10 billion packets per day. Collaborated with customers to prioritize development capabilities, such as improved protocol parsing capabilities, support for new TCP/IP protocols, and modern configuration management.
              </li>
              
              <li className="leading-relaxed">
                • Engineered a suite of Python tools to aggregate and structure IPv4 network scanner activity logs across enterprise networks to profile bad actors, resulting in 90 percent reduction in long-term storage of SYN-only TCP flows associated with scanning. Collaborated with analysts to understand data requirements needed to identify behavioral patterns and support detection engineering efforts.
              </li>
            </ul>
          </div>
        </div>
      </FadeInSection>

      {/* Education Section */}
      <FadeInSection className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-text-white border-b border-text-gray pb-2">
          Education
        </h2>
        
        <div className="border border-gray-600 rounded-lg p-6">
          <div className="flex justify-between items-start flex-wrap">
            <div>
              <h3 className="text-xl font-semibold text-text-white">George Mason University</h3>
              <p className="text-text-gray italic">Bachelor of Science in Computer Science</p>
            </div>
          </div>
        </div>
      </FadeInSection>

      {/* Publications Section */}
      <FadeInSection className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-text-white border-b border-text-gray pb-2">
          Publications
        </h2>
        
        <div className="space-y-6">
          <div className="border border-gray-600 rounded-lg p-6">
            <div className="flex justify-between items-start mb-4 flex-wrap">
              <h3 className="text-xl font-semibold text-text-white">
                <a 
                  href="https://www.sei.cmu.edu/library/flocon2024-demystifying-the-shape-of-traffic/" 
                  className="text-primary hover:text-text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Demystifying the Shape of Traffic in the Cloud
                </a>
              </h3>
              <span className="text-text-gray italic">Speaker, 2024</span>
            </div>
            <p className="text-text-gray leading-relaxed">
              Presented at FloCon on how AWS Flow Log sensor architecture introduces routing and security monitoring challenges, highlighting key differences between cloud and on-premise network telemetry.
            </p>
          </div>
          
          <div className="border border-gray-600 rounded-lg p-6">
            <div className="flex justify-between items-start mb-4 flex-wrap">
              <h3 className="text-xl font-semibold text-text-white">
                <a 
                  href="https://afscsoftware.dso.mil/crosstalk/" 
                  className="text-primary hover:text-text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Keeping Up With the Cloud
                </a>
              </h3>
              <span className="text-text-gray italic">Author, 2025</span>
            </div>
            <p className="text-text-gray leading-relaxed">
              Coauthored an article on cloud flow collection, focusing on flow generation nuances, sensor deployment, and architecture considerations for optimal network security telemetry collection.
            </p>
          </div>
        </div>
      </FadeInSection>

      {/* Projects Section */}
      <FadeInSection className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-text-white border-b border-text-gray pb-2">
          Projects
        </h2>
        
        <div className="border border-gray-600 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-text-white mb-4">
            <a 
              href="https://github.com/tgben/chess" 
              className="text-primary hover:text-text-white transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Chess Opening Success Analysis System
            </a>
          </h3>
          
          <p className="text-text-gray leading-relaxed">
            Designed and implemented a scalable ETL pipeline using Apache Kafka middleware to decouple PGN parsing from PostgreSQL database operations to process and analyze 125 million chess games, reaching 2,500 games/second throughput. Optimized query performance with custom PostgreSQL materialized views for precomputed statistics and developed a React frontend to visualize opening success rates across player levels. <span className="italic">(Python, Kafka, SQL, Docker, React)</span>
          </p>
        </div>
      </FadeInSection>

    </div>
  );
};

export default CV;